import React, { memo, useMemo, useState, useCallback, Fragment } from "react";
import { Button, Card, Grid, IconButton, Typography } from "@mui/material";
import CategoryCardTypes from "../../globals/types/CategoryCardTypes";
import ModalPopup from "../ModalPoup";
import { v4 as uuidv4 } from 'uuid';
import AddIcon from '@mui/icons-material/Add';
import JobAppFieldsTypes from "../../globals/types/JobAppFieldsTypes";
import { ref, remove, update } from "firebase/database";
import { db } from "../../globals/firebase";
import JobCard from "./JobCard";
import { useDrop } from 'react-dnd';
import { ItemTypes } from "../../globals/types/DraggableItemTypes";
import { jobCategories } from "../../globals/globalVariables";
import dayjs from "dayjs";

const CategoryCard = memo(function CategoryCard({
    title,
    titleColor,
    isOwner,
    toggleModal,
    setFormState,
    jobs,
    username,
    listId
}: CategoryCardTypes) {
    const jobsOfMyCategory = useMemo(() => {
        if (!jobs) return undefined;
        return jobs[title.toLowerCase()];
    }, [jobs, title]);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [modalBody, setModalBody] = useState("");
    const [modalFooter, setModalFooter] = useState<React.ReactNode>(null);

    const toggleConfirm = useCallback(() => {
        setShowConfirmModal(prev => !prev);
    }, []);

    const [{ isOver }, drop] = useDrop({
        accept: ItemTypes.CARD,
        drop: (item: JobAppFieldsTypes) => {
            if (!item || !username || !listId) return;
            const updates: Record<string, any> = {};
            const updatedItem = { ...item, category: title };
            updates['/users/' + username + '/listVals/' + listId + '/jobs/' + title.toLowerCase() + '/' + item.uuid] = updatedItem;
            jobCategories.forEach(category => {
                if (category.name.toLowerCase() !== title.toLowerCase()) {
                    updates['/users/' + username + '/listVals/' + listId + '/jobs/' + category.name.toLowerCase() + '/' + item.uuid] = {};
                }
            });
            update(ref(db), updates);
        },
        collect: monitor => ({
            isOver: !!monitor.isOver(),
        }),
    }, [title, username, listId]);

    const handleOpenJobApp = useCallback((job: JobAppFieldsTypes) => {
        return () => { 
            toggleModal();
            setFormState({ type: 'CHANGE_ALL', payload: job });
        };
    }, [toggleModal, setFormState]);

    const handleOpenJobLink = useCallback((job: JobAppFieldsTypes) => {
        return () => {
            setModalBody(`This link goes to ${job.link}. Are you sure you want to go there?`);
            setModalFooter(
                <div>
                    <Button color="success" onClick={() => {
                        setShowConfirmModal(false);
                        window.open(job.link, "_blank");
                    }}>
                        Yes, take me there!
                    </Button>
                    <Button color="error" onClick={() => setShowConfirmModal(false)}>
                        No, take me back!
                    </Button>
                </div>
            );
            setShowConfirmModal(true);
        };
    }, []);

    const handleDeleteJobApp = useCallback((job: JobAppFieldsTypes) => {
        return () => {
            setModalBody(`Are you sure you want to delete this application (${job.position} at ${job.company})?`);
            setModalFooter(
                <div>
                    <Button color="error" onClick={() => {
                        remove(ref(db, '/users/' + username + '/listVals/' + listId + '/jobs/' + job.category.toLowerCase() + '/' + job.uuid))
                            .then(() => {
                                setShowConfirmModal(false);
                            })
                            .catch((error) => {
                                console.error(error);
                                alert("Something went wrong: please try again later.");
                            });
                    }}>
                        Yes, delete it!
                    </Button>
                    <Button color="info" onClick={() => setShowConfirmModal(false)}>
                        No, keep it!
                    </Button>
                </div>
            );
            setShowConfirmModal(true);
        };
    }, [username, listId]);

    const onAddClick = useCallback(() => {
        toggleModal();
        setFormState({
            type: 'CHANGE_ALL',
            payload: {
                category: title,
                date: dayjs().format('YYYY-MM-DD'),
                color: titleColor,
                company: "",
                position: "",
                link: "",
                notes: "",
                recruiterContact: "",
                recruiterName: "",
                deadlines: [],
                uuid: uuidv4()
            }
        });
    }, [toggleModal, setFormState, title, titleColor]);

    const jobKeys = useMemo(() => {
        return jobsOfMyCategory ? Object.keys(jobsOfMyCategory) : [];
    }, [jobsOfMyCategory]);

    return (
        <Card 
            variant="outlined" 
            sx={{
                width: '100%', 
                height: '100%', 
                backgroundColor: isOver ? '#e0e0e0' : '#f7f7f7', 
                position: 'relative', 
                overflowY: 'hidden',
                transition: 'background-color 0.15s ease'
            }} 
            ref={drop}
        >
            <ModalPopup
                showModal={showConfirmModal}
                toggleModal={toggleConfirm}
                header="Hold Up!"
                body={
                    <Typography color="black" sx={{ wordWrap: 'break-word' }}>
                        {modalBody}
                    </Typography>
                }
                footer={modalFooter}
            />
            <p className="mini center" style={{ color: titleColor, marginTop: '2%' }}>{title}</p>    
            {isOwner && (
                <IconButton 
                    style={{
                        position: 'absolute',
                        top: '0px',
                        right: '0px',
                    }}
                    onClick={onAddClick}
                    aria-label={`Add job to ${title}`}
                >
                    <AddIcon /> 
                </IconButton>
            )}
            <Grid container spacing={1} sx={{ marginTop: '1%' }}>
                {jobKeys.map((jobKey) => {
                    const job = jobsOfMyCategory![jobKey];
                    return (
                        <Fragment key={job.uuid || jobKey}>
                            <Grid item xs={0.5}></Grid>
                            <Grid item xs={11}>
                                <JobCard 
                                    isOwner={isOwner} 
                                    job={job} 
                                    handleOpenJobApp={handleOpenJobApp} 
                                    handleOpenJobLink={handleOpenJobLink} 
                                    handleDeleteJobApp={handleDeleteJobApp} 
                                />
                            </Grid>
                            <Grid item xs={0.5}></Grid>
                        </Fragment>
                    );
                })}
            </Grid>
        </Card>
    );
});

export default CategoryCard;